import { useEffect, useState } from "react";
import { Check, Landmark, Loader2 } from "lucide-react";

import { PasskeyEnrollPrompt } from "../auth/PasskeyEnrollPrompt";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";

import {
  acknowledgeFirstPlaidLink,
  hasAcknowledgedFirstPlaidLink,
} from "./plaid-connect-success-dialog.helpers";

/**
 * Phase of the post-Plaid-Link flow:
 *  - `linking`: exchange + initial sync running; Plaid Link's own modal
 *    has closed but our work isn't done. Shows a spinner with the
 *    institution name so the user knows progress is happening.
 *  - `first-link-success`: educational success content + Done / Connect
 *    another CTAs. Only shown the first time per device.
 *  - `null`: dialog hidden.
 *
 * The dialog stays open across the linking → success transition so the
 * user sees one continuous experience (no portal flash from closing one
 * dialog and opening another).
 */
export type PlaidConnectFlowPhase = "linking" | "first-link-success" | null;

export function PlaidConnectOpeningDialog({ open }: { open: boolean }) {
  return (
    <Dialog open={open}>
      <DialogContent
        description="Plaid can take a few seconds to load. The secure bank window will appear automatically."
        showClose={false}
        title={
          <span className="flex items-center gap-2">
            <Loader2
              aria-hidden
              className="h-5 w-5 animate-spin text-primary motion-reduce:animate-none"
            />
            <span>Opening Plaid…</span>
          </span>
        }
      >
        <div
          aria-busy="true"
          className="flex items-center gap-3 rounded-2xl border border-primary/15 bg-primary/5 px-4 py-4"
          role="status"
        >
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background text-primary shadow-sm ring-1 ring-primary/15"
          >
            <Landmark className="h-5 w-5" />
          </span>
          <p className="text-sm text-muted-foreground">
            Starting the secure bank connection…
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Combined progress + success dialog. The two phases share a single
 * portal-mounted dialog so the user sees an uninterrupted "Linking your
 * bank → Done" experience. Subsequent (non-first) links never reach
 * `first-link-success`; the call site closes the dialog and pushes a
 * toast instead — see `usePlaidConnectFlowState`.
 */
export function PlaidConnectFlowDialog({
  institutionName,
  onClose,
  onConnectAnother,
  phase,
}: {
  institutionName: string;
  onClose: () => void;
  onConnectAnother?: () => void;
  phase: PlaidConnectFlowPhase;
}) {
  return (
    <Dialog
      open={phase !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        description={
          phase === "linking"
            ? `Pulling balances and any shared debt accounts from ${institutionName}. This usually takes a few seconds — don't close this tab.`
            : phase === "first-link-success"
              ? `We're syncing balances and any shared debt accounts from ${institutionName}. Your readiness picture will refresh in a moment — give the sync a few seconds, then your linked banks list and rows will reflect the new evidence.`
              : undefined
        }
        title={
          phase === "linking" ? (
            <span className="flex items-center gap-2">
              <Loader2
                aria-hidden
                className="h-5 w-5 animate-spin text-primary"
              />
              <span>Linking {institutionName}…</span>
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <span
                aria-hidden
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary"
              >
                <Check className="h-4 w-4" />
              </span>
              <span>Linked {institutionName}</span>
            </span>
          )
        }
      >
        {phase === "first-link-success" ? (
          <>
            <ul className="mb-5 space-y-2 text-sm text-muted-foreground">
              <li>
                • Bank-verified balances replace any self-reported savings.
              </li>
              <li>
                • Shared credit card and loan accounts can populate the debts
                row automatically.
              </li>
              <li>
                • You can disconnect any time from Settings → Linked banks.
              </li>
            </ul>
            {/* The link and its sync have succeeded — the one moment the
                benefit of a faster sign-in is concrete. */}
            <PasskeyEnrollPrompt className="mb-5" trigger="plaid_link" />
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              {onConnectAnother ? (
                <Button onClick={onConnectAnother} variant="outline">
                  Connect another bank
                </Button>
              ) : null}
              <Button data-slot="plaid-success-done" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            Establishing the secure connection…
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * usePlaidConnectFlowState coordinates the four-phase post-Plaid-Link
 * dialog: idle → linking → (first-link-success | idle). Call sites:
 *  - call `recordLinkStart(name)` the moment Plaid Link's onSuccess
 *    fires, OR pass `isLinking` / `linkingInstitutionName` straight
 *    from `usePlaidConnect` if you prefer a derived view
 *  - call `recordLinkSuccess()` once exchange + sync settle (from the
 *    hook's `onLinked` callback); the return tells the caller whether
 *    to ALSO push a toast (subsequent links) or sit on the modal
 *    (first link)
 *  - call `closeDialog()` when the user dismisses the success modal
 */
export function usePlaidConnectFlowState() {
  const [phase, setPhase] = useState<PlaidConnectFlowPhase>(null);
  const [institutionName, setInstitutionName] = useState<string | null>(null);
  return {
    institutionName,
    phase,
    recordLinkStart(name: string) {
      setInstitutionName(name);
      setPhase("linking");
    },
    /**
     * Records a successful link. Returns `{ isFirstLink }` — true on
     * the user's first-ever successful link on this device (educational
     * modal stays open), false thereafter (modal closes; caller pushes
     * a toast instead). The first-link ack is persisted here so the
     * "first time" state never re-fires even if the dialog never
     * mounted (e.g. the caller chose toast-only).
     */
    recordLinkSuccess(): { isFirstLink: boolean } {
      const isFirstLink = !hasAcknowledgedFirstPlaidLink();
      if (isFirstLink) {
        acknowledgeFirstPlaidLink();
        setPhase("first-link-success");
      } else {
        setPhase(null);
        setInstitutionName(null);
      }
      return { isFirstLink };
    },
    /**
     * Records a failed/aborted link (reverification miss, exchange
     * error). Closes the dialog without recording an ack so the next
     * attempt still counts as "first link."
     */
    recordLinkAbort() {
      setPhase(null);
      setInstitutionName(null);
    },
    closeDialog() {
      setPhase(null);
      setInstitutionName(null);
    },
  };
}

// ─── Back-compat shims ──────────────────────────────────────────────
// usePlaidConnectSuccessState is kept as a thin wrapper over
// usePlaidConnectFlowState until tests migrate. The internal contract
// (`recordLink → {isFirstLink}`) is preserved; the difference is the
// state hook now also tracks the linking phase.

export function usePlaidConnectSuccessState() {
  const flow = usePlaidConnectFlowState();
  return {
    institutionName: flow.institutionName,
    showFirstLinkModal: flow.phase === "first-link-success",
    recordLink(institutionName: string) {
      flow.recordLinkStart(institutionName);
      return flow.recordLinkSuccess();
    },
    closeModal: flow.closeDialog,
  };
}

/**
 * @deprecated Use PlaidConnectFlowDialog with phase="first-link-success"
 *   directly so the linking → success transition stays in one portal.
 */
export function PlaidConnectSuccessDialog(props: {
  institutionName: string;
  onClose: () => void;
  onConnectAnother?: () => void;
  open: boolean;
}) {
  // Persist the acknowledgement on first open so existing tests of the
  // standalone modal still work — see the equivalent comment on
  // recordLinkSuccess for the rationale.
  useEffect(() => {
    if (props.open) {
      acknowledgeFirstPlaidLink();
    }
  }, [props.open]);
  return (
    <PlaidConnectFlowDialog
      institutionName={props.institutionName}
      onClose={props.onClose}
      onConnectAnother={props.onConnectAnother}
      phase={props.open ? "first-link-success" : null}
    />
  );
}
